import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { ApiResponse } from '@/api/types'

interface CustomerLedgerReportParams {
  fromDate: string
  toDate: string
  branchId: number
  pageNumber: number
  pageSize: number
  customerId?: number
}

interface AccountLedgerReportParams {
  fromDate: string
  toDate: string
  branchId: number
  pageNumber: number
  pageSize: number
  accountId?: number
}

interface SalesRevenueReportParams {
  fromDate: string
  toDate: string
  branchId: number
  pageNumber: number
  pageSize: number
  customerId?: number
}

export interface SalesRevenueEntry {
  customer: string | null
  description: string
  type: string | null
  voucher: number
  amount: number
  createdDate: string
  branchName: string | null
}

interface VendorLedgerReportParams {
  fromDate: string
  toDate: string
  branchId: number
  pageNumber: number
  pageSize: number
  vendorId?: number
}

interface CustomerLedgerEntry {
  account: string | null
  customer: string | null
  costCenter: string | null
  costLocation: string | null
  vendor: string | null
  accountCode: string | null
  description: string
  voucher: number
  type: string | null
  debit: number
  credit: number
  balance: number
  project: string | null
  ref: string | null
  department: string | null
  notes: string | null
  createdDate: string
  branchName: string | null
}

interface AccountLedgerEntry {
  account: string | null
  customer: string | null
  costCenter: string | null
  costLocation: string | null
  vendor: string | null
  accountCode: string | null
  description: string
  voucher: number
  type: string | null
  debit: number
  credit: number
  balance: number
  project: string | null
  ref: string | null
  department: string | null
  notes: string | null
  createdDate: string
  branchName: string | null
}

interface VendorLedgerEntry {
  account: string | null
  customer: string | null
  costCenter: string | null
  costLocation: string | null
  vendor: string | null
  accountCode: string | null
  description: string
  voucher: number
  type: string | null
  debit: number
  credit: number
  balance: number
  project: string | null
  ref: string | null
  department: string | null
  notes: string | null
  createdDate: string
  branchName: string | null
}

interface TrialBalanceReportParams {
  fromDate: string
  toDate: string
  branchId: number
  pageNumber: number
  pageSize: number
  withBalancesOnly?: boolean
}

export interface TrialBalanceEntry {
  branchName: string | null
  projectName: string | null
  costCenterName: string | null
  costProfitLocationName: string | null
  code: string
  head: string | null
  subHead: string | null
  subSubHead: string | null
  type: string | null
  account: string
  oDebit: number
  oCredit: number
  oTotal: number
  aDebit: number
  aCredit: number
  aTotal: number
  cDebit: number
  cCredit: number
  cTotal: number
}

interface AgedReceivablesReportParams {
  agingDate: string
  branchId: number
  pageNumber: number
  pageSize: number
  customerId?: number
}

export interface AgedReceivablesItem {
  invoiceNo: string
  customerName: string
  invoiceDate: string
  dueDate: string
  outstandingAmount: number
  days_0_30: number
  days_31_60: number
  days_61_90: number
  days_91_180: number
  days_181_360: number
  days_360_Plus: number
  total: number
  branchName: string
}

export interface AgedReceivablesReportResponse {
  agingDate: string
  items: AgedReceivablesItem[]
  totalOutstanding: number
  total_0_30: number
  total_31_60: number
  total_61_90: number
  total_91_180: number
  total_181_360: number
  total_360_Plus: number
  totalAmount: number
  totalRecords?: number
  pageNumber?: number
  pageSize?: number
}

interface AgedPayablesReportParams {
  agingDate: string
  branchId: number
  pageNumber: number
  pageSize: number
  vendorId?: number
}

export interface AgedPayablesItem {
  billNo: string
  vendorName: string
  billDate: string
  dueDate: string
  outstandingAmount: number
  days0To30: number
  days31To60: number
  days61To90: number
  days91To180: number
  days181To360: number
  days360Plus: number
  total: number
  branchName: string
}

export interface AgedPayablesReportResponse {
  agingDate: string
  rows: AgedPayablesItem[]
  totalOutstanding: number
  total0To30: number
  total31To60: number
  total61To90: number
  total91To180: number
  total181To360: number
  total360Plus: number
  grandTotal: number
  totalRecords?: number
  pageNumber?: number
  pageSize?: number
}

export interface LedgerReportResponse<T> {
  data: T[]
  totalRecords?: number
  pageNumber?: number
  pageSize?: number
  totalDebit?: number
  totalCredit?: number
  overallBalance?: number
  totalAmount?: number
  /** Trial balance footer totals (full report, not current page) */
  totalODebit?: number
  totalOCredit?: number
  totalOTotal?: number
  totalADebit?: number
  totalACredit?: number
  totalATotal?: number
  totalCDebit?: number
  totalCCredit?: number
  totalCTotal?: number
}

function receivableOutstanding(item: AgedReceivablesItem): number {
  if (item.outstandingAmount != null) return Number(item.outstandingAmount) || 0
  return (
    (item.days_0_30 || 0) +
    (item.days_31_60 || 0) +
    (item.days_61_90 || 0) +
    (item.days_91_180 || 0) +
    (item.days_181_360 || 0) +
    (item.days_360_Plus || 0)
  )
}

function payableOutstanding(item: AgedPayablesItem): number {
  if (item.outstandingAmount != null) return Number(item.outstandingAmount) || 0
  return (
    (item.days0To30 || 0) +
    (item.days31To60 || 0) +
    (item.days61To90 || 0) +
    (item.days91To180 || 0) +
    (item.days181To360 || 0) +
    (item.days360Plus || 0)
  )
}

function mapReceivableItems(items: AgedReceivablesItem[]): AgedReceivablesItem[] {
  return items.map((item) => ({ ...item, outstandingAmount: receivableOutstanding(item) }))
}

function mapPayableRows(rows: AgedPayablesItem[]): AgedPayablesItem[] {
  return rows.map((item) => ({ ...item, outstandingAmount: payableOutstanding(item) }))
}

function mapLedgerReportResponse<T>(
  payload: LedgerReportResponse<T> | undefined,
  params: { pageNumber: number; pageSize: number },
  fallbackData: T[] = [],
): LedgerReportResponse<T> {
  if (!payload) {
    return {
      data: fallbackData,
      totalRecords: 0,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    }
  }

  const data = Array.isArray(payload.data) ? payload.data : fallbackData

  return {
    data,
    totalRecords: payload.totalRecords ?? data.length,
    pageNumber: payload.pageNumber ?? params.pageNumber,
    pageSize: payload.pageSize ?? params.pageSize,
    totalDebit: payload.totalDebit,
    totalCredit: payload.totalCredit,
    overallBalance: payload.overallBalance,
    totalAmount: payload.totalAmount,
    totalODebit: payload.totalODebit,
    totalOCredit: payload.totalOCredit,
    totalOTotal: payload.totalOTotal,
    totalADebit: payload.totalADebit,
    totalACredit: payload.totalACredit,
    totalATotal: payload.totalATotal,
    totalCDebit: payload.totalCDebit,
    totalCCredit: payload.totalCCredit,
    totalCTotal: payload.totalCTotal,
  }
}

/**
 * Report service for handling report-related API operations
 */
export class ReportService {
  /**
   * Get customer ledger report
   */
  async getCustomerLedgerReport(
    params: CustomerLedgerReportParams
  ): Promise<LedgerReportResponse<CustomerLedgerEntry>> {
    const url = endpoints.reports.customerLedger(
      params.fromDate,
      params.toDate,
      params.branchId,
      params.pageNumber,
      params.pageSize,
      params.customerId
    )
    const response = await apiClient.get<ApiResponse<LedgerReportResponse<CustomerLedgerEntry>> | LedgerReportResponse<CustomerLedgerEntry> | CustomerLedgerEntry[]>(url)

    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'totalRecords' in response.data) {
      return mapLedgerReportResponse(response.data as LedgerReportResponse<CustomerLedgerEntry>, params)
    }

    if (Array.isArray(response.data)) {
      return mapLedgerReportResponse(undefined, params, response.data)
    }

    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const apiResponse = response.data as ApiResponse<LedgerReportResponse<CustomerLedgerEntry>>
      if (apiResponse.data && typeof apiResponse.data === 'object' && 'data' in apiResponse.data) {
        return mapLedgerReportResponse(apiResponse.data as LedgerReportResponse<CustomerLedgerEntry>, params)
      }
    }

    return mapLedgerReportResponse(undefined, params)
  }

  async getSalesRevenueReport(
    params: SalesRevenueReportParams
  ): Promise<LedgerReportResponse<SalesRevenueEntry>> {
    const url = endpoints.reports.salesRevenue(
      params.fromDate,
      params.toDate,
      params.branchId,
      params.pageNumber,
      params.pageSize,
      params.customerId
    )
    const response = await apiClient.get<ApiResponse<LedgerReportResponse<SalesRevenueEntry>> | LedgerReportResponse<SalesRevenueEntry> | SalesRevenueEntry[]>(url)

    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'totalRecords' in response.data) {
      return mapLedgerReportResponse(response.data as LedgerReportResponse<SalesRevenueEntry>, params)
    }

    if (Array.isArray(response.data)) {
      return mapLedgerReportResponse(undefined, params, response.data)
    }

    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const apiResponse = response.data as ApiResponse<LedgerReportResponse<SalesRevenueEntry>>
      if (apiResponse.data && typeof apiResponse.data === 'object' && 'data' in apiResponse.data) {
        return mapLedgerReportResponse(apiResponse.data as LedgerReportResponse<SalesRevenueEntry>, params)
      }
    }

    return mapLedgerReportResponse(undefined, params)
  }

  /**
   * Get account ledger report
   */
  async getAccountLedgerReport(
    params: AccountLedgerReportParams
  ): Promise<LedgerReportResponse<AccountLedgerEntry>> {
    const url = endpoints.reports.accountLedger(
      params.fromDate,
      params.toDate,
      params.branchId,
      params.pageNumber,
      params.pageSize,
      params.accountId
    )
    const response = await apiClient.get<ApiResponse<LedgerReportResponse<AccountLedgerEntry>> | LedgerReportResponse<AccountLedgerEntry> | AccountLedgerEntry[]>(url)

    // Handle direct LedgerReportResponse (includes totalDebit/totalCredit/overallBalance from API)
    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'totalRecords' in response.data) {
      return mapLedgerReportResponse(response.data as LedgerReportResponse<AccountLedgerEntry>, params)
    }

    // Handle direct array response
    if (Array.isArray(response.data)) {
      return mapLedgerReportResponse(undefined, params, response.data)
    }

    // Handle ApiResponse wrapper
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const apiResponse = response.data as ApiResponse<LedgerReportResponse<AccountLedgerEntry>>
      if (apiResponse.data && typeof apiResponse.data === 'object' && 'data' in apiResponse.data) {
        return mapLedgerReportResponse(apiResponse.data as LedgerReportResponse<AccountLedgerEntry>, params)
      }
    }

    return mapLedgerReportResponse(undefined, params)
  }

  /**
   * Get vendor ledger report
   */
  async getVendorLedgerReport(
    params: VendorLedgerReportParams
  ): Promise<LedgerReportResponse<VendorLedgerEntry>> {
    const url = endpoints.reports.vendorLedger(
      params.fromDate,
      params.toDate,
      params.branchId,
      params.pageNumber,
      params.pageSize,
      params.vendorId
    )
    const response = await apiClient.get<ApiResponse<LedgerReportResponse<VendorLedgerEntry>> | LedgerReportResponse<VendorLedgerEntry> | VendorLedgerEntry[]>(url)

    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'totalRecords' in response.data) {
      return mapLedgerReportResponse(response.data as LedgerReportResponse<VendorLedgerEntry>, params)
    }

    if (Array.isArray(response.data)) {
      return mapLedgerReportResponse(undefined, params, response.data)
    }

    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const apiResponse = response.data as ApiResponse<LedgerReportResponse<VendorLedgerEntry>>
      if (apiResponse.data && typeof apiResponse.data === 'object' && 'data' in apiResponse.data) {
        return mapLedgerReportResponse(apiResponse.data as LedgerReportResponse<VendorLedgerEntry>, params)
      }
    }

    return mapLedgerReportResponse(undefined, params)
  }

  /**
   * Get trial balance report
   */
  async getTrialBalanceReport(
    params: TrialBalanceReportParams
  ): Promise<LedgerReportResponse<TrialBalanceEntry>> {
    const url = endpoints.reports.trialBalance(
      params.fromDate,
      params.toDate,
      params.branchId,
      params.pageNumber,
      params.pageSize,
      params.withBalancesOnly
    )
    const response = await apiClient.get<ApiResponse<LedgerReportResponse<TrialBalanceEntry>> | LedgerReportResponse<TrialBalanceEntry> | TrialBalanceEntry[]>(url)

    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'totalRecords' in response.data) {
      return mapLedgerReportResponse(response.data as LedgerReportResponse<TrialBalanceEntry>, params)
    }

    if (Array.isArray(response.data)) {
      return mapLedgerReportResponse(undefined, params, response.data)
    }

    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const apiResponse = response.data as ApiResponse<LedgerReportResponse<TrialBalanceEntry>>
      if (apiResponse.data && typeof apiResponse.data === 'object' && 'data' in apiResponse.data) {
        return mapLedgerReportResponse(apiResponse.data as LedgerReportResponse<TrialBalanceEntry>, params)
      }
    }

    return mapLedgerReportResponse(undefined, params)
  }

  /**
   * Get aged receivables report
   */
  async getAgedReceivablesReport(
    params: AgedReceivablesReportParams
  ): Promise<AgedReceivablesReportResponse> {
    const url = endpoints.reports.agedReceivables(
      params.agingDate,
      params.branchId,
      params.pageNumber,
      params.pageSize,
      params.customerId
    )
    const response = await apiClient.get<any>(url)
    
    // Handle nested structure: { totalRecords, pageNumber, pageSize, report: { items, ...totals } }
    if (response.data && typeof response.data === 'object' && 'report' in response.data) {
      const reportData = response.data.report
      const items = mapReceivableItems(Array.isArray(reportData.items) ? reportData.items : [])
      return {
        agingDate: reportData.agingDate || params.agingDate,
        items,
        totalOutstanding: reportData.totalOutstanding ?? items.reduce((sum, item) => sum + (item.outstandingAmount || 0), 0),
        total_0_30: reportData.total_0_30 || 0,
        total_31_60: reportData.total_31_60 || 0,
        total_61_90: reportData.total_61_90 || 0,
        total_91_180: reportData.total_91_180 || 0,
        total_181_360: reportData.total_181_360 || 0,
        total_360_Plus: reportData.total_360_Plus || 0,
        totalAmount: reportData.totalAmount || 0,
        totalRecords: response.data.totalRecords || items.length || 0,
        pageNumber: response.data.pageNumber ?? params.pageNumber,
        pageSize: response.data.pageSize ?? params.pageSize,
      }
    }
    
    // Handle direct array response (API returns array of items directly)
    if (Array.isArray(response.data)) {
      // Calculate totals from items
      const items = mapReceivableItems(response.data as AgedReceivablesItem[])
      const totals = items.reduce(
        (acc, item) => {
          acc.totalOutstanding += item.outstandingAmount || 0
          acc.total_0_30 += item.days_0_30 || 0
          acc.total_31_60 += item.days_31_60 || 0
          acc.total_61_90 += item.days_61_90 || 0
          acc.total_91_180 += item.days_91_180 || 0
          acc.total_181_360 += item.days_181_360 || 0
          acc.total_360_Plus += item.days_360_Plus || 0
          acc.totalAmount += item.total || 0
          return acc
        },
        {
          totalOutstanding: 0,
          total_0_30: 0,
          total_31_60: 0,
          total_61_90: 0,
          total_91_180: 0,
          total_181_360: 0,
          total_360_Plus: 0,
          totalAmount: 0,
        }
      )
      
      return {
        agingDate: params.agingDate,
        items: items,
        ...totals,
        totalRecords: items.length,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      }
    }
    
    // Handle wrapped array response
    if ('data' in response.data && Array.isArray(response.data.data)) {
      const items = mapReceivableItems(response.data.data as AgedReceivablesItem[])
      const totals = items.reduce(
        (acc, item) => {
          acc.totalOutstanding += item.outstandingAmount || 0
          acc.total_0_30 += item.days_0_30 || 0
          acc.total_31_60 += item.days_31_60 || 0
          acc.total_61_90 += item.days_61_90 || 0
          acc.total_91_180 += item.days_91_180 || 0
          acc.total_181_360 += item.days_181_360 || 0
          acc.total_360_Plus += item.days_360_Plus || 0
          acc.totalAmount += item.total || 0
          return acc
        },
        {
          totalOutstanding: 0,
          total_0_30: 0,
          total_31_60: 0,
          total_61_90: 0,
          total_91_180: 0,
          total_181_360: 0,
          total_360_Plus: 0,
          totalAmount: 0,
        }
      )
      
      return {
        agingDate: params.agingDate,
        items: items,
        ...totals,
        totalRecords: (response.data as any).totalRecords ?? items.length,
        pageNumber: (response.data as any).pageNumber ?? params.pageNumber,
        pageSize: (response.data as any).pageSize ?? params.pageSize,
      }
    }
    
    // Handle object response with items array
    if ('items' in response.data && Array.isArray(response.data.items)) {
      return {
        ...(response.data as AgedReceivablesReportResponse),
        totalRecords: (response.data as any).totalRecords ?? response.data.items.length,
        pageNumber: (response.data as any).pageNumber ?? params.pageNumber,
        pageSize: (response.data as any).pageSize ?? params.pageSize,
      }
    }
    
    // Return empty structure if response doesn't match expected format
    return {
      agingDate: params.agingDate,
      items: [],
      totalOutstanding: 0,
      total_0_30: 0,
      total_31_60: 0,
      total_61_90: 0,
      total_91_180: 0,
      total_181_360: 0,
      total_360_Plus: 0,
      totalAmount: 0,
      totalRecords: 0,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    }
  }

  /**
   * Get aged payables report
   */
  async getAgedPayablesReport(
    params: AgedPayablesReportParams
  ): Promise<AgedPayablesReportResponse> {
    const url = endpoints.reports.agedPayables(
      params.agingDate,
      params.branchId,
      params.pageNumber,
      params.pageSize,
      params.vendorId
    )
    const response = await apiClient.get<any>(url)
    
    // Handle nested structure: { totalRecords, pageNumber, pageSize, report: { rows, ...totals } }
    if (response.data && typeof response.data === 'object' && 'report' in response.data) {
      const reportData = response.data.report
      const rows = mapPayableRows(Array.isArray(reportData.rows) ? reportData.rows : [])
      return {
        agingDate: reportData.agingDate || params.agingDate,
        rows,
        totalOutstanding: reportData.totalOutstanding ?? rows.reduce((sum, item) => sum + (item.outstandingAmount || 0), 0),
        total0To30: reportData.total0To30 || 0,
        total31To60: reportData.total31To60 || 0,
        total61To90: reportData.total61To90 || 0,
        total91To180: reportData.total91To180 || 0,
        total181To360: reportData.total181To360 || 0,
        total360Plus: reportData.total360Plus || 0,
        grandTotal: reportData.grandTotal || 0,
        totalRecords: response.data.totalRecords || rows.length || 0,
        pageNumber: response.data.pageNumber ?? params.pageNumber,
        pageSize: response.data.pageSize ?? params.pageSize,
      }
    }
    
    // Handle direct array response (API returns array of items directly)
    if (Array.isArray(response.data)) {
      // Calculate totals from items
      const rows = mapPayableRows(response.data as AgedPayablesItem[])
      const totals = rows.reduce(
        (acc, item) => {
          acc.totalOutstanding += item.outstandingAmount || 0
          acc.total0To30 += item.days0To30 || 0
          acc.total31To60 += item.days31To60 || 0
          acc.total61To90 += item.days61To90 || 0
          acc.total91To180 += item.days91To180 || 0
          acc.total181To360 += item.days181To360 || 0
          acc.total360Plus += item.days360Plus || 0
          acc.grandTotal += item.total || 0
          return acc
        },
        {
          totalOutstanding: 0,
          total0To30: 0,
          total31To60: 0,
          total61To90: 0,
          total91To180: 0,
          total181To360: 0,
          total360Plus: 0,
          grandTotal: 0,
        }
      )
      
      return {
        agingDate: params.agingDate,
        rows: rows,
        ...totals,
        totalRecords: rows.length,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      }
    }
    
    // Handle wrapped array response
    if ('data' in response.data && Array.isArray(response.data.data)) {
      const rows = mapPayableRows(response.data.data as AgedPayablesItem[])
      const totals = rows.reduce(
        (acc, item) => {
          acc.totalOutstanding += item.outstandingAmount || 0
          acc.total0To30 += item.days0To30 || 0
          acc.total31To60 += item.days31To60 || 0
          acc.total61To90 += item.days61To90 || 0
          acc.total91To180 += item.days91To180 || 0
          acc.total181To360 += item.days181To360 || 0
          acc.total360Plus += item.days360Plus || 0
          acc.grandTotal += item.total || 0
          return acc
        },
        {
          totalOutstanding: 0,
          total0To30: 0,
          total31To60: 0,
          total61To90: 0,
          total91To180: 0,
          total181To360: 0,
          total360Plus: 0,
          grandTotal: 0,
        }
      )
      
      return {
        agingDate: params.agingDate,
        rows: rows,
        ...totals,
        totalRecords: (response.data as any).totalRecords ?? rows.length,
        pageNumber: (response.data as any).pageNumber ?? params.pageNumber,
        pageSize: (response.data as any).pageSize ?? params.pageSize,
      }
    }
    
    // Handle object response with rows array
    if ('rows' in response.data && Array.isArray(response.data.rows)) {
      return {
        ...(response.data as AgedPayablesReportResponse),
        totalRecords: (response.data as any).totalRecords ?? response.data.rows.length,
        pageNumber: (response.data as any).pageNumber ?? params.pageNumber,
        pageSize: (response.data as any).pageSize ?? params.pageSize,
      }
    }
    
    // Return empty structure if response doesn't match expected format
    return {
      agingDate: params.agingDate,
      rows: [],
      totalOutstanding: 0,
      total0To30: 0,
      total31To60: 0,
      total61To90: 0,
      total91To180: 0,
      total181To360: 0,
      total360Plus: 0,
      grandTotal: 0,
      totalRecords: 0,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    }
  }

  /**
   * Get VAT report
   */
  async getVatReport(
    params: {
      fromDate: string
      toDate: string
      branchId: number
      pageNumber: number
      pageSize: number
    }
  ): Promise<LedgerReportResponse<VatReportEntry>> {
    const url = endpoints.reports.vatReport(
      params.fromDate,
      params.toDate,
      params.branchId,
      params.pageNumber,
      params.pageSize
    )
    const response = await apiClient.get<ApiResponse<LedgerReportResponse<VatReportEntry>> | LedgerReportResponse<VatReportEntry> | VatReportEntry[]>(url)
    
    // Handle direct LedgerReportResponse
    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'totalRecords' in response.data) {
      const reportResponse = response.data as LedgerReportResponse<VatReportEntry>
      return {
        data: Array.isArray(reportResponse.data) ? reportResponse.data : [],
        totalRecords: reportResponse.totalRecords,
        pageNumber: reportResponse.pageNumber ?? params.pageNumber,
        pageSize: reportResponse.pageSize ?? params.pageSize,
      }
    }
    
    // Handle direct array response
    if (Array.isArray(response.data)) {
      return {
        data: response.data,
        totalRecords: response.data.length,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      }
    }
    
    // Handle ApiResponse wrapper
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const apiResponse = response.data as ApiResponse<LedgerReportResponse<VatReportEntry> | VatReportEntry[]>
      if (Array.isArray(apiResponse.data)) {
        return {
          data: apiResponse.data,
          totalRecords: apiResponse.data.length,
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
        }
      }
      return {
        data: Array.isArray(apiResponse.data.data) ? apiResponse.data.data : [],
        totalRecords: apiResponse.data.totalRecords || apiResponse.data.data?.length || 0,
        pageNumber: apiResponse.data.pageNumber ?? params.pageNumber,
        pageSize: apiResponse.data.pageSize ?? params.pageSize,
      }
    }
    
    return {
      data: [],
      totalRecords: 0,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    }
  }

  /**
   * Download VAT report as Excel
   */
  async downloadVatReportExcel(
    params: {
      fromDate: string
      toDate: string
      branchId: number
    }
  ): Promise<Blob> {
    const url = endpoints.reports.printVatReport(
      params.fromDate,
      params.toDate,
      params.branchId
    )
    const response = await apiClient.get(url, {
      responseType: 'blob',
    })
    
    return response.data
  }

  /**
   * Get Fixed Asset report
   */
  async getFixedAssetReport(
    params: {
      fromDate: string
      toDate: string
      branchId: number
      pageNumber: number
      pageSize: number
    }
  ): Promise<FixedAssetReportResponse> {
    const url = endpoints.reports.fixedAssetReport(
      params.fromDate,
      params.toDate,
      params.branchId,
      params.pageNumber,
      params.pageSize
    )
    const response = await apiClient.get<ApiResponse<FixedAssetReportResponse> | FixedAssetReportResponse>(url)
    
    // Handle direct FixedAssetReportResponse
    if (response.data && typeof response.data === 'object' && 'data' in response.data && 'totalRecords' in response.data) {
      const reportResponse = response.data as FixedAssetReportResponse
      return {
        data: Array.isArray(reportResponse.data) ? reportResponse.data : [],
        totalRecords: reportResponse.totalRecords || 0,
        pageNumber: reportResponse.pageNumber ?? params.pageNumber,
        pageSize: reportResponse.pageSize ?? params.pageSize,
        totalPurchase: reportResponse.totalPurchase || 0,
        totalAccumulatedDepreciation: reportResponse.totalAccumulatedDepreciation || 0,
        totalNetBookValue: reportResponse.totalNetBookValue || 0,
      }
    }
    
    // Handle ApiResponse wrapper
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const apiResponse = response.data as ApiResponse<FixedAssetReportResponse>
      if (apiResponse.data) {
        return {
          data: Array.isArray(apiResponse.data.data) ? apiResponse.data.data : [],
          totalRecords: apiResponse.data.totalRecords || 0,
          pageNumber: apiResponse.data.pageNumber ?? params.pageNumber,
          pageSize: apiResponse.data.pageSize ?? params.pageSize,
          totalPurchase: apiResponse.data.totalPurchase || 0,
          totalAccumulatedDepreciation: apiResponse.data.totalAccumulatedDepreciation || 0,
          totalNetBookValue: apiResponse.data.totalNetBookValue || 0,
        }
      }
    }
    
    return {
      data: [],
      totalRecords: 0,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      totalPurchase: 0,
      totalAccumulatedDepreciation: 0,
      totalNetBookValue: 0,
    }
  }

  /**
   * Download Fixed Asset report as Excel
   */
  async downloadFixedAssetReportExcel(
    params: {
      fromDate: string
      toDate: string
      branchId: number
    }
  ): Promise<Blob> {
    const url = endpoints.reports.printFixedAssetReport(
      params.fromDate,
      params.toDate,
      params.branchId
    )
    const response = await apiClient.get(url, {
      responseType: 'blob',
    })
    
    return response.data
  }
}

export interface VatReportEntry {
  id: number
  date: string
  vendorCustomer: string | null
  type: string | null
  number: string | null
  reference: string | null
  note: string | null
  taxableAmount: number
  vatDebit: number
  vatCredit: number
  balance: number
}

export interface FixedAssetReportEntry {
  assetNo: string | null
  assetName: string | null
  category: string | null
  purchasePrice: number
  purchaseDate: string
  lifeYears: number
  lifeMonths: number
  accumulatedDepreciation: number
  depreciationMethod: string | null
  openingBookValue: number
  additions: number
  disposals: number
  ytdDepreciation: number
  netBookValue: number
  project: string | null
  costCenter: string | null
  costLocation: string | null
}

export interface FixedAssetReportResponse {
  data: FixedAssetReportEntry[]
  totalRecords: number
  pageNumber: number
  pageSize: number
  totalPurchase: number
  totalAccumulatedDepreciation: number
  totalNetBookValue: number
}

export const reportService = new ReportService()

